import type { TFunction } from 'i18next';
import i18n from '../../i18n';

/**
 * Domaines du namespace i18n `status` (libellés système / enums).
 * Ne pas y mettre de contenu Super Admin.
 */
export type StatusDomain =
  | 'paymentMethod'
  | 'expensePaymentMethod'
  | 'expenseCategory'
  | 'appointmentStatus'
  | 'messagesClientsStatus'
  | 'salaryAdjustmentType'
  | 'adjustmentStatus'
  | 'payrollStatus'
  | 'campaignType'
  | 'campaignStatus'
  | 'campaignUsageType'
  | 'campaignChannel'
  | 'campaignObjective'
  | 'campaignObjectiveDesc'
  | 'campaignUsageTypeDesc'
  | 'campaignChannelDesc'
  | 'computedCampaignStatus'
  | 'stockMovementType'
  | 'stockLossReason'
  | 'inventoryStatus'
  | 'countStatus'
  | 'inventoryAdjustmentReason'
  | 'receivableStatus'
  | 'paymentPlanStatus'
  | 'saleOperationType'
  | 'pickupRequestStatus'
  | 'pickupPublicStatus'
  | 'paymentTerms'
  | 'deliveryStatus'
  | 'siteType'
  | 'validationMode'
  | 'supportTicketStatus'
  | 'supportTicketCategory'
  | 'manufacturingOrderStatus'
  | 'weeklyDeadlineDay'
  | 'receiptFormat';

type TranslateFn = TFunction | ((key: string, options?: Record<string, unknown>) => string);

/**
 * Libellé traduit pour un statut / enum système.
 * Sans `t`, utilise l'instance i18n (locale courante).
 */
export function getStatusLabel(
  domain: StatusDomain,
  key: string,
  t?: TranslateFn
): string {
  const path = `${domain}.${key}`;
  if (t) {
    return String(t(path, { ns: 'status', defaultValue: key }));
  }
  return String(i18n.t(path, { ns: 'status', defaultValue: key }));
}

/**
 * Map clé → libellé avec getters (réactif à la locale i18n courante).
 * Remplace les anciens `*_LABELS` statiques FR.
 */
export function createI18nLabelMap<K extends string>(
  domain: StatusDomain,
  keys: readonly K[]
): Record<K, string> {
  const map = {} as Record<K, string>;
  for (const key of keys) {
    Object.defineProperty(map, key, {
      enumerable: true,
      configurable: true,
      get(): string {
        return getStatusLabel(domain, key);
      },
    });
  }
  return map;
}

/** Construit une map à partir de `t` (pour un rendu React lié à useTranslation). */
export function buildStatusLabelMap<K extends string>(
  domain: StatusDomain,
  keys: readonly K[],
  t: TranslateFn
): Record<K, string> {
  return Object.fromEntries(
    keys.map((key) => [key, getStatusLabel(domain, key, t)])
  ) as Record<K, string>;
}
